import { Graphics, Container } from 'pixi.js';

/**
 * Body part containers for a dinosaur fighter.
 * Each part is a separate Graphics so we can animate (rotate jaws, move legs, etc.)
 */
export interface DinoParts {
  root: Container;
  body: Graphics;
  head: Graphics;
  jaw: Graphics;
  tail: Graphics;
  armLeft: Graphics;
  armRight: Graphics;
  legLeft: Graphics;
  legRight: Graphics;
  eye: Graphics;
}

export interface DinoConfig {
  bodyColor: number;
  bellyColor: number;
  accentColor: number;
  eyeColor: number;
  width: number;
  height: number;
  type: 'trex' | 'spinosaurus';
}

const TREX_CONFIG: DinoConfig = {
  bodyColor: 0x2d8c2d,
  bellyColor: 0x5cb85c,
  accentColor: 0x1a5c1a,
  eyeColor: 0xffcc00,
  width: 80,
  height: 100,
  type: 'trex',
};

const SPINO_CONFIG: DinoConfig = {
  bodyColor: 0x8c2d2d,
  bellyColor: 0xc75050,
  accentColor: 0x5c1a1a,
  eyeColor: 0xff6600,
  width: 90,
  height: 110,
  type: 'spinosaurus',
};

const SPINO_SAIL_COLOR = 0xa83020;
const SPINO_SAIL_HIGHLIGHT = 0xd04830;

export function getDinoConfig(type: 'trex' | 'spinosaurus'): DinoConfig {
  return type === 'trex' ? TREX_CONFIG : SPINO_CONFIG;
}

/**
 * Draw a solid block (rectangle) with optional alpha.
 */
function block(
  g: Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  color: number,
  alpha = 1,
): void {
  g.rect(x, y, w, h);
  g.fill({ color, alpha });
}

/**
 * Draw a distinct square block with a visible darker border.
 * The border is drawn at higher alpha so each square reads as a separate unit.
 */
function sq(
  g: Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  fillColor: number,
  edgeColor: number,
): void {
  g.rect(x, y, w, h);
  g.fill({ color: fillColor });
  g.rect(x, y, w, h);
  g.stroke({ width: 1.5, color: edgeColor, alpha: 0.7 });
}

/**
 * Draw a lighter-shade square block for visual alternation.
 * Uses the belly/highlight color as a subtle tint over the fill.
 */
function sqLt(
  g: Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  fillColor: number,
  edgeColor: number,
  ltColor: number,
): void {
  // Fill with base color
  g.rect(x, y, w, h);
  g.fill({ color: fillColor });
  // Light overlay for the alternation effect
  g.rect(x + 1, y + 1, w - 2, h - 2);
  g.fill({ color: ltColor, alpha: 0.15 });
  // Edge
  g.rect(x, y, w, h);
  g.stroke({ width: 1.5, color: edgeColor, alpha: 0.7 });
}

// =====================================================================
//  T-REX  (multi-square block construction — faces RIGHT, anchor = center-bottom)
//
//  Every body part is built from 2-5 clearly-distinct square blocks.
//  Adjacent blocks overlap by 2px for connectivity while the
//  strong edge strokes + alternating light/dark squares keep them
//  visually separable.
//
//  Anatomy (all coords relative to center-bottom = 0,0):
//    Head top: y = -100 (matches stats.height)
//    Feet: y = 0
// =====================================================================

export function createTRexGraphics(): DinoParts {
  const cfg = TREX_CONFIG;
  const root = new Container();
  const c = cfg.bodyColor;
  const a = cfg.accentColor;
  const b = cfg.bellyColor;

  // --- TAIL (5 overlapping square blocks tapering left) ---
  const tail = new Graphics();
  // Block 1: base — attaches into hips
  sq(tail, -20, -52, 18, 16, c, a);
  // Block 2: overlaps by 4px
  sqLt(tail, -34, -50, 18, 13, c, a, b);
  // Block 3: overlaps by 4px
  sq(tail, -48, -48, 18, 11, c, a);
  // Block 4: overlaps by 4px
  sqLt(tail, -60, -46, 16, 9, c, a, b);
  // Block 5: tip
  sq(tail, -72, -44, 16, 7, a, a);
  // Belly highlight on base
  block(tail, -18, -42, 12, 4, b, 0.4);

  tail.pivot.set(-28, -46);
  tail.position.set(-28, -46);
  root.addChild(tail);

  // --- LEGS (thigh: 2 squares, knee: 1 square, shin: 2 squares, foot: 2 squares) ---
  const legLeft = createTRexBlockLeg(cfg);
  const legRight = createTRexBlockLeg(cfg);
  legLeft.pivot.set(0, -8);
  legLeft.position.set(-10, -8);
  legRight.pivot.set(0, -8);
  legRight.position.set(8, -8);
  root.addChild(legLeft);
  root.addChild(legRight);

  // --- BODY: pelvis (2 sq) + hips (2 sq) + belly (2 sq) + chest (2 sq) + neck (2 sq) ---
  // Built bottom-to-top with each row overlapping the one below by 4px.
  // Pelvis extends down to y=-16 to overlap leg thigh tops (world y=-18).
  const body = new Graphics();

  // Pelvis — connects body to legs (2 squares side by side)
  sq(body, -14, -34, 14, 18, c, a);
  sqLt(body, -2, -34, 14, 18, c, a, b);

  // Hips — 2 squares, overlaps pelvis by 4px
  sqLt(body, -16, -50, 16, 20, c, a, b);
  sq(body, -2, -50, 16, 20, c, a);
  block(body, -8, -42, 16, 8, b, 0.35);

  // Belly — 2 squares, overlaps hips by 4px
  sq(body, -20, -66, 20, 20, c, a);
  sqLt(body, -2, -66, 20, 20, c, a, b);
  block(body, -10, -58, 20, 10, b, 0.45);

  // Chest — 2 squares, overlaps belly by 4px
  sqLt(body, -22, -82, 22, 20, c, a, b);
  sq(body, -2, -82, 22, 20, c, a);
  // Spine ridge
  block(body, -20, -84, 38, 4, a, 0.5);

  // Neck — 2 squares stacked, overlaps chest by 4px
  sq(body, 6, -94, 16, 16, c, a);
  sqLt(body, 8, -104, 12, 14, c, a, b);
  // Throat highlight
  block(body, 16, -92, 4, 10, b, 0.4);

  root.addChild(body);

  // --- ARMS (shoulder sq + upper arm sq + forearm sq + claw) ---
  const armLeft = new Graphics();
  sq(armLeft, -1, -2, 10, 10, c, a);
  sqLt(armLeft, 0, 6, 8, 10, c, a, b);
  sq(armLeft, 1, 14, 6, 8, c, a);
  sq(armLeft, 2, 20, 4, 5, a, a);
  armLeft.position.set(18, -72);
  armLeft.pivot.set(4, 0);

  const armRight = new Graphics();
  sqLt(armRight, -1, -2, 10, 10, c, a, b);
  sq(armRight, 0, 6, 8, 10, c, a);
  sqLt(armRight, 1, 14, 6, 8, c, a, b);
  sq(armRight, 2, 20, 4, 5, a, a);
  armRight.position.set(22, -70);
  armRight.pivot.set(4, 0);

  root.addChild(armLeft);
  root.addChild(armRight);

  // --- HEAD (cranium: 2 sq, snout: 2 sq — all overlapping) ---
  const head = new Graphics();
  // Cranium rear square
  sq(head, -10, -24, 18, 18, c, a);
  // Cranium front square — overlaps by 4px
  sqLt(head, 4, -24, 16, 18, c, a, b);
  // Snout square — overlaps cranium by 4px
  sq(head, 14, -20, 14, 14, c, a);
  // Snout tip square — overlaps by 2px
  sqLt(head, 26, -18, 10, 10, c, a, b);
  // Brow ridge squares
  block(head, -8, -26, 14, 4, a, 0.7);
  block(head, 4, -26, 14, 4, a, 0.5);
  // Nostril
  block(head, 32, -16, 3, 3, a);
  // Upper teeth — drawn on head so they stay fixed with snout (don't move with jaw)
  for (let tx = 4; tx < 34; tx += 8) {
    block(head, tx, -12, 4, 3, 0xffffff);
  }

  head.pivot.set(8, -12);
  head.position.set(14, -88);
  root.addChild(head);

  // --- JAW (3 overlapping squares + tip) ---
  // Jaw local y=0 maps to headY in world space (-88 for T-Rex).
  // Head snout bottom is at world y≈-84, so jaw shapes start at local y≈4
  // to visually meet the underside of the snout.
  const jaw = new Graphics();
  sq(jaw, -4, 4, 14, 7, c, a);
  sqLt(jaw, 6, 4, 14, 7, c, a, b);
  sq(jaw, 16, 5, 12, 6, c, a);
  // Jaw tip
  block(jaw, 26, 5, 8, 5, c);
  // Lower teeth (upper teeth are on the head Graphics so they stay with the snout)
  for (let tx = 4; tx < 32; tx += 6) {
    block(jaw, tx, 6, 3, 3, 0xeeeeee);
  }

  jaw.pivot.set(0, 0);
  jaw.position.set(6, -88);
  root.addChild(jaw);

  // --- EYE ---
  const eye = new Graphics();
  block(eye, -4, -4, 8, 8, cfg.eyeColor);
  block(eye, -1, -3, 4, 6, 0x000000);
  block(eye, 1, -3, 2, 2, 0xffffff);

  eye.position.set(22, -98);
  root.addChild(eye);

  return { root, body, head, jaw, tail, armLeft, armRight, legLeft, legRight, eye };
}

function createTRexBlockLeg(cfg: DinoConfig): Graphics {
  const leg = new Graphics();
  const c = cfg.bodyColor;
  const a = cfg.accentColor;
  const b = cfg.bellyColor;

  // Thigh — upper square
  sq(leg, -8, -18, 16, 14, c, a);
  // Thigh — lower square (overlaps by 4px)
  sqLt(leg, -7, -8, 14, 12, c, a, b);

  // Knee joint square — overlaps thigh by 2px
  sq(leg, -6, 2, 12, 10, c, a);

  // Shin — upper square (overlaps knee by 2px)
  sqLt(leg, -6, 10, 12, 10, c, a, b);
  // Shin — lower square (overlaps by 2px)
  sq(leg, -5, 18, 10, 6, c, a);

  // Foot — 2 squares side by side (overlaps shin by 2px)
  sq(leg, -10, 22, 10, 8, a, a);
  sq(leg, -2, 22, 10, 8, a, a);

  // Claws (3 toes)
  block(leg, -12, 28, 5, 4, a);
  block(leg, -2, 28, 5, 4, a);
  block(leg, 6, 28, 5, 4, a);

  return leg;
}

// =====================================================================
//  SPINOSAURUS  (multi-square block construction — faces RIGHT, anchor = center-bottom)
//
//  Same multi-square philosophy as T-Rex. Alternating sq / sqLt for
//  visual distinction between adjacent blocks.
//
//  Anatomy (all coords relative to center-bottom = 0,0):
//    Sail top: ~y = -128
//    Head top: y = -110 (matches stats.height)
//    Feet: y = 0
// =====================================================================

export function createSpinosaurusGraphics(): DinoParts {
  const cfg = SPINO_CONFIG;
  const root = new Container();
  const sc = cfg.bodyColor;
  const sa = cfg.accentColor;
  const sb = cfg.bellyColor;

  // --- TAIL (5 overlapping squares tapering left + fin) ---
  const tail = new Graphics();
  sq(tail, -22, -56, 20, 14, sc, sa);
  sqLt(tail, -38, -54, 20, 12, sc, sa, sb);
  sq(tail, -52, -52, 18, 10, sc, sa);
  sqLt(tail, -64, -50, 16, 8, sc, sa, sb);
  sq(tail, -76, -48, 16, 6, sa, sa);
  // Vertical fin at tip (2 stacked squares)
  sq(tail, -78, -58, 6, 12, sa, sa);
  block(tail, -80, -56, 4, 8, sa, 0.7);
  // Belly on base
  block(tail, -20, -46, 14, 4, sb, 0.4);

  tail.pivot.set(-28, -50);
  tail.position.set(-28, -50);
  root.addChild(tail);

  // --- LEGS ---
  const legLeft = createSpinoBlockLeg(cfg);
  const legRight = createSpinoBlockLeg(cfg);
  legLeft.pivot.set(0, -10);
  legLeft.position.set(-12, -10);
  legRight.pivot.set(0, -10);
  legRight.position.set(10, -10);
  root.addChild(legLeft);
  root.addChild(legRight);

  // --- BODY: pelvis (2 sq) + hips (2 sq) + belly (2 sq) + chest (2 sq) + neck (2 sq) + sail ---
  // Pelvis extends down to y=-18 to overlap leg thigh tops (world y=-20).
  const body = new Graphics();

  // Pelvis — connects body to legs (2 squares side by side)
  sq(body, -16, -38, 16, 20, sc, sa);
  sqLt(body, -2, -38, 16, 20, sc, sa, sb);

  // Hips — 2 squares, overlaps pelvis by 4px
  sqLt(body, -18, -56, 18, 22, sc, sa, sb);
  sq(body, -2, -56, 18, 22, sc, sa);
  block(body, -10, -46, 20, 10, sb, 0.35);

  // Belly — 2 squares, overlaps hips by 4px
  sq(body, -22, -72, 22, 20, sc, sa);
  sqLt(body, -2, -72, 22, 20, sc, sa, sb);
  block(body, -12, -64, 24, 10, sb, 0.45);

  // Chest — 2 squares, overlaps belly by 4px
  sqLt(body, -24, -88, 24, 20, sc, sa, sb);
  sq(body, -2, -88, 24, 20, sc, sa);
  // Spine ridge
  block(body, -22, -90, 42, 4, sa, 0.5);

  // Neck — 2 squares stacked, overlaps chest by 4px
  sq(body, 8, -100, 18, 16, sc, sa);
  sqLt(body, 10, -112, 14, 16, sc, sa, sb);
  // Throat highlight
  block(body, 22, -98, 4, 12, sb, 0.4);

  // --- SAIL (the signature feature!) ---
  // Built from columns of small square blocks
  const sailDef: Array<[number, number]> = [
    [-20, 10],
    [-16, 18],
    [-12, 26],
    [-8, 32],
    [-4, 36],
    [0, 38],
    [4, 38],
    [8, 36],
    [12, 32],
    [16, 26],
    [20, 18],
    [24, 10],
  ];
  for (const [sx, sh] of sailDef) {
    block(body, sx, -90 - sh, 4, sh, SPINO_SAIL_COLOR);
    block(body, sx, -90 - sh, 4, 2, SPINO_SAIL_HIGHLIGHT);
  }
  for (let i = 0; i < sailDef.length; i += 3) {
    const [sx, sh] = sailDef[i];
    block(body, sx + 1, -90 - sh + 2, 2, sh - 2, sa, 0.3);
  }

  root.addChild(body);

  // --- ARMS (shoulder sq + upper arm sq + forearm sq + claw) ---
  const armLeft = new Graphics();
  sq(armLeft, -1, -2, 12, 12, sc, sa);
  sqLt(armLeft, 0, 8, 10, 12, sc, sa, sb);
  sq(armLeft, 1, 18, 8, 10, sc, sa);
  sq(armLeft, 2, 26, 6, 5, sa, sa);
  armLeft.position.set(20, -76);
  armLeft.pivot.set(5, 0);

  const armRight = new Graphics();
  sqLt(armRight, -1, -2, 12, 12, sc, sa, sb);
  sq(armRight, 0, 8, 10, 12, sc, sa);
  sqLt(armRight, 1, 18, 8, 10, sc, sa, sb);
  sq(armRight, 2, 26, 6, 5, sa, sa);
  armRight.position.set(24, -74);
  armRight.pivot.set(5, 0);

  root.addChild(armLeft);
  root.addChild(armRight);

  // --- HEAD (cranium: 2 sq, snout: 2 sq, tip: 1 sq — elongated croc) ---
  const head = new Graphics();
  sq(head, -8, -20, 16, 16, sc, sa);
  sqLt(head, 4, -20, 16, 16, sc, sa, sb);
  sq(head, 16, -18, 14, 12, sc, sa);
  sqLt(head, 28, -16, 10, 10, sc, sa, sb);
  // Far tip
  sq(head, 36, -14, 8, 8, sc, sa);
  // Brow ridge
  block(head, -6, -22, 14, 4, sa, 0.7);
  block(head, 6, -22, 14, 4, sa, 0.5);
  // Snout crest
  block(head, 16, -22, 8, 4, sa, 0.5);
  // Nostril
  block(head, 40, -12, 3, 3, sa);
  // Upper teeth — drawn on head so they stay fixed with snout (don't move with jaw)
  for (let tx = 6; tx < 40; tx += 7) {
    block(head, tx, -8, 4, 4, 0xffffff);
  }

  head.pivot.set(8, -10);
  head.position.set(14, -98);
  root.addChild(head);

  // --- JAW (4 overlapping squares for long croc jaw) ---
  // Jaw local y=0 maps to headY in world space (-98 for Spino).
  // Head snout bottom is at world y≈-94, so jaw shapes start at local y≈4
  // to visually meet the underside of the snout.
  const jaw = new Graphics();
  const baseY = 4;
  sq(jaw, -4, baseY + 2, 14, 6, sc, sa);
  sqLt(jaw, 6, baseY + 2, 14, 6, sc, sa, sb);
  sq(jaw, 16, baseY + 2, 12, 6, sc, sa);
  sqLt(jaw, 26, baseY + 3, 10, 5, sc, sa, sb);
  // Jaw far tip
  block(jaw, 34, baseY + 3, 8, 4, sc);
  // Underside
  block(jaw, 0, baseY + 5, 30, 2, sb, 0.3);
  // Lower teeth (upper teeth are on the head Graphics so they stay with the snout)
  for (let tx = 4; tx < 36; tx += 5) {
    block(jaw, tx, baseY + 3, 3, 3, 0xeeeeee);
  }

  jaw.pivot.set(0, 0);
  jaw.position.set(8, -98);
  root.addChild(jaw);

  // --- EYE ---
  const eye = new Graphics();
  block(eye, -4, -4, 8, 8, cfg.eyeColor);
  block(eye, -1, -3, 3, 6, 0x000000);
  block(eye, 1, -3, 2, 2, 0xffffff);

  eye.position.set(18, -108);
  root.addChild(eye);

  return { root, body, head, jaw, tail, armLeft, armRight, legLeft, legRight, eye };
}

function createSpinoBlockLeg(cfg: DinoConfig): Graphics {
  const leg = new Graphics();
  const c = cfg.bodyColor;
  const a = cfg.accentColor;
  const b = cfg.bellyColor;

  // Thigh — upper square
  sq(leg, -8, -20, 16, 14, c, a);
  // Thigh — lower square (overlaps by 4px)
  sqLt(leg, -7, -10, 14, 12, c, a, b);

  // Knee joint square — overlaps thigh by 2px
  sq(leg, -6, 0, 12, 10, c, a);

  // Shin — upper square (overlaps knee by 2px)
  sqLt(leg, -6, 8, 12, 10, c, a, b);
  // Shin — lower square (overlaps by 2px)
  sq(leg, -5, 16, 10, 8, c, a);

  // Foot — 2 squares side by side (overlaps shin by 2px)
  sq(leg, -10, 22, 10, 8, a, a);
  sq(leg, -2, 22, 10, 8, a, a);

  // Claws (3 toes)
  block(leg, -12, 28, 5, 4, a);
  block(leg, -2, 28, 5, 4, a);
  block(leg, 6, 28, 5, 4, a);

  return leg;
}
